import React, { useMemo, useState } from 'react';
import { errorMessage, getProjectStatistics, listProfilingCategories, saveQuotaSettings } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { buildFeasibilityInput } from '../feasibility/feasibilityInput';
import { CellFeasibility, FeasibilitySummary } from '../feasibility/FeasibilityNote';
import { useFeasibility } from '../feasibility/useFeasibility';
import type { FeasibilityState } from '../feasibility/useFeasibility';
import { Badge, Button, Card, InlineAlert, Table, Tabs, Td, Th, cx, useToast } from '../../ui';
import type { GeoState, ProjectStatistics, QuotaCellProgress, QuotaMode, TargetGroup } from '../../types';
import { CellFill } from './CellFill';
import { CellPicker } from './CellPicker';
import type { PickerGroup } from './CellPicker';
import { LooseQuotaTable } from './LooseQuotaTable';
import { groupSavedAnswers } from './profilingModel';
import type { ProfilingCategory } from './profilingModel';
import { QuotaPresetPicker } from './QuotaPresetPicker';
import {
  autoDistribute, genderTotals, largestLooseQuota, looseFromSaved, looseQuotasToPayload, modeFromSaved, newKey, newLooseRow, parseQuotaErrors,
  quotasFromSaved, quotasToPayload, rowTotal, splitsFromSaved, totalCompletes, validateLooseQuotas, validateQuotas,
} from './quotasModel';
import type { LooseDimension, LooseRow, QuotaRow } from './quotasModel';
import { SaveQuotaPresetDialog } from './SaveQuotaPresetDialog';
import type { PresetSource } from './SaveQuotaPresetDialog';
import { useWizard } from './WizardContext';

const cell = 'w-20 rounded-md border bg-surface px-2 py-1.5 text-right text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const BAND_CHOICES = [2, 3, 4, 5, 6, 7, 8];
const MODE_TABS = [
  { key: 'interlocking', label: 'Interlocking' },
  { key: 'nonInterlocking', label: 'Non-interlocking' },
];

interface SummaryProblems {
  errors: Record<string, string>;
  warnings: string[];
  attempted: boolean;
  serverMessage: string;
  serverGeneral: string[];
  serverRowProblems: number;
}

/** Every warning and error for the quotas, in one place under the totals (shared by both modes). */
function Problems({ errors, warnings, attempted, rowCount, serverMessage, serverGeneral, serverRowProblems }: SummaryProblems & { rowCount: number }) {
  const rowErrors = Object.keys(errors).filter((k) => k !== '_form').length;
  const serverFailed = serverMessage !== '' || serverGeneral.length > 0 || serverRowProblems > 0;
  return (
    <>
      {/* "No rows yet" is only an error once the user tries to save; the others show live. */}
      {errors._form && (attempted || rowCount > 0) && <InlineAlert>{errors._form}</InlineAlert>}
      {attempted && rowErrors > 0 && <InlineAlert>{rowErrors} row{rowErrors === 1 ? ' has' : 's have'} a problem. See the highlighted rows.</InlineAlert>}
      {serverFailed && (
        <InlineAlert>
          <b>{serverMessage || 'The server did not save these quotas.'}</b>
          {serverGeneral.length > 0 && <ul className="ml-4 mt-1 list-disc">{serverGeneral.map((m) => <li key={m}>{m}</li>)}</ul>}
          {serverRowProblems > 0 && <p className="mt-1">{serverRowProblems} row problem{serverRowProblems === 1 ? ' is' : 's are'} marked in the table.</p>}
        </InlineAlert>
      )}
      {warnings.map((w) => <InlineAlert key={w} tone="warn">{w}</InlineAlert>)}
    </>
  );
}

/** Right-hand summary for INTERLOCKING quotas: total against wanted, gender split and every warning. */
function QuotaSummary({ total, wanted, rows, splitActive, feasibility, ...problems }: SummaryProblems & { total: number; wanted: number; rows: QuotaRow[]; splitActive: boolean; feasibility: FeasibilityState }) {
  const { male, female } = genderTotals(rows);
  const state = total === wanted ? 'ok' : total > wanted ? 'over' : 'under';
  const pct = wanted > 0 ? Math.min(100, Math.round((total / wanted) * 100)) : 0;
  const rowErrors = Object.keys(problems.errors).filter((k) => k !== '_form').length;
  const serverFailed = problems.serverMessage !== '' || problems.serverGeneral.length > 0 || problems.serverRowProblems > 0;
  return (
    <Card className="p-5" aria-label="Quota summary" role="region">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-ink">Summary</h2>
        <Badge tone={state === 'ok' ? 'ok' : state === 'over' ? 'danger' : 'warn'}>{state === 'ok' ? 'Matches' : state === 'over' ? 'Over' : 'Under'}</Badge>
      </div>
      <p className="mt-3 flex items-baseline justify-between">
        <span className="text-3xl font-extrabold tracking-tight text-ink tabular-nums">{total.toLocaleString()}</span>
        <span className="text-sm text-muted">of {wanted.toLocaleString()} wanted</span>
      </p>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-chip" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Quota total compared with wanted completes">
        <div className={cx('h-full rounded-full transition-all', state === 'over' ? 'bg-danger' : state === 'ok' ? 'bg-ok' : 'bg-brand-600')} style={{ width: `${pct}%` }} />
      </div>
      <dl className="mt-4 divide-y divide-line/70 text-sm">
        <div className="flex justify-between py-2"><dt className="text-muted">Male</dt><dd className="font-semibold tabular-nums">{male.toLocaleString()}</dd></div>
        <div className="flex justify-between py-2"><dt className="text-muted">Female</dt><dd className="font-semibold tabular-nums">{female.toLocaleString()}</dd></div>
        <div className="flex justify-between py-2"><dt className="text-muted">Rows (cells)</dt><dd className="font-semibold tabular-nums">{rows.length}</dd></div>
      </dl>
      <div className="mt-3 space-y-2">
        <Problems {...problems} rowCount={rows.length} />
        {splitActive && <InlineAlert tone="info"><b>Each row is its own cell.</b> Rows are added up once, so do not describe the same people in two rows (for example one row per region and another per profiling answer).</InlineAlert>}
        {state === 'ok' && !problems.errors._form && rowErrors === 0 && !serverFailed && <InlineAlert tone="info">Quotas add up to the wanted completes.</InlineAlert>}
      </div>
      <FeasibilitySummary feasibility={feasibility} />
    </Card>
  );
}

/** Right-hand summary for NON-INTERLOCKING quotas: rows are independent, so the rule is about the largest one. */
function LooseSummary({ rows, wanted, feasibility, ...problems }: SummaryProblems & { rows: LooseRow[]; wanted: number; feasibility: FeasibilityState }) {
  const largest = largestLooseQuota(rows);
  const over = wanted > 0 && largest > wanted;
  const pct = wanted > 0 ? Math.min(100, Math.round((largest / wanted) * 100)) : 0;
  return (
    <Card className="p-5" aria-label="Quota summary" role="region">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-ink">Summary</h2>
        <Badge tone={over ? 'danger' : rows.length ? 'ok' : 'neutral'}>{over ? 'Too large' : rows.length ? 'Within limit' : 'No quotas'}</Badge>
      </div>
      <p className="mt-3 flex items-baseline justify-between">
        <span className="text-3xl font-extrabold tracking-tight text-ink tabular-nums">{largest.toLocaleString()}</span>
        <span className="text-sm text-muted">largest quota, of {wanted.toLocaleString()} wanted</span>
      </p>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-chip" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Largest quota compared with wanted completes">
        <div className={cx('h-full rounded-full transition-all', over ? 'bg-danger' : 'bg-brand-600')} style={{ width: `${pct}%` }} />
      </div>
      <dl className="mt-4 divide-y divide-line/70 text-sm">
        <div className="flex justify-between py-2"><dt className="text-muted">Quotas</dt><dd className="font-semibold tabular-nums">{rows.length}</dd></div>
      </dl>
      <div className="mt-3 space-y-2">
        <Problems {...problems} rowCount={rows.length} />
        <InlineAlert tone="info"><b>Independent quotas.</b> Each row is its own quota on one dimension. Rows are not added up and age bands may overlap; no single quota may exceed the wanted completes.</InlineAlert>
      </div>
      <FeasibilitySummary feasibility={feasibility} />
    </Card>
  );
}

function SplitSwitch({ label, hint, checked, disabled, onChange }: { label: string; hint: string; checked: boolean; disabled?: boolean; onChange: (on: boolean) => void }) {
  return (
    <label className={cx('flex items-center gap-2.5 rounded-lg border px-3 py-1.5 text-sm font-semibold', disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer', checked ? 'border-brand-300 bg-brand-50/60' : 'border-line')}>
      <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}<span className="block text-[11px] font-normal text-muted">{hint}</span></span>
    </label>
  );
}

export function StepQuotas() {
  const { project, targetGroup, reload, goStep } = useWizard();
  const { notify } = useToast();
  const basics = targetGroup.basicSetting || {};
  const ctx = { ageMin: basics.ageMinLimit, ageMax: basics.ageMaxLimit, wanted: basics.noOfWantedCompletes };
  const saved = targetGroup.quotaSettings;
  const hasSavedQuotas = Boolean(saved && (saved.data || []).length > 0);

  // The only states / answers a quota row may use are the ones this target group already has.
  const availableStates = useMemo(() => {
    const region = targetGroup.region;
    const map = new Map<string, { id: string; name: string; whole: boolean }>();
    ((region && region.states) || []).forEach((s: GeoState) => { if (s && s.id) map.set(s.id, { id: s.id, name: s.name || s.id, whole: false }); });
    ((region && region.wholeStates) || []).forEach((s: GeoState) => { if (s && s.id) map.set(s.id, { id: s.id, name: s.name || s.id, whole: true }); });
    return [...map.values()];
  }, [targetGroup.region]);
  const savedAnswers = useMemo(() => targetGroup.profilingAnswers || [], [targetGroup.profilingAnswers]);

  // Each mode keeps its own rows, so switching back and forth does not lose what was typed; only the active mode is saved.
  const initialMode = modeFromSaved(saved);
  const initialSplits = splitsFromSaved(initialMode === 'interlocking' ? saved : null);
  const [mode, setMode] = useState<QuotaMode>(initialMode);
  const [rows, setRows] = useState<QuotaRow[]>(() => (initialMode === 'interlocking' ? quotasFromSaved(saved) : []));
  const [looseRows, setLooseRows] = useState<LooseRow[]>(() => (initialMode === 'nonInterlocking' ? looseFromSaved(saved) : []));
  const [splitRegion, setSplitRegion] = useState(initialSplits.splitRegion);
  const [splitProfiling, setSplitProfiling] = useState(initialSplits.splitProfiling);
  const [bands, setBands] = useState(4);
  const [attempted, setAttempted] = useState(false);
  const [formError, setFormError] = useState('');
  const [serverErrors, setServerErrors] = useState<{ message: string; byRow: Record<string, string[]>; general: string[] } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [submitting, run] = useSubmitGuard();
  const interlocking = mode === 'interlocking';

  const canSplitRegion = availableStates.length > 0;
  const canSplitProfiling = savedAnswers.length > 0;
  const regionOn = splitRegion && canSplitRegion;
  const profilingOn = splitProfiling && canSplitProfiling;
  const splits = { splitRegion: regionOn, splitProfiling: profilingOn };

  // Answers are grouped under their question; the categories are only needed (and fetched) once a picker can show them.
  const categories = useAsyncData<ProfilingCategory[]>((signal) => listProfilingCategories({ signal }), [], { enabled: canSplitProfiling && (interlocking ? splitProfiling : true) });
  const answerGroups: PickerGroup[] = useMemo(
    () => groupSavedAnswers(savedAnswers, categories.data).map((g) => ({ title: g.question, options: g.answers })),
    [savedAnswers, categories.data]
  );
  const stateGroups: PickerGroup[] = useMemo(
    () => [{ title: 'Target group regions', options: availableStates.map((s) => ({ id: s.id, name: s.name, note: s.whole ? '(entire state)' : undefined })) }],
    [availableStates]
  );

  const { errors, warnings } = interlocking ? validateQuotas(rows, ctx, splits) : validateLooseQuotas(looseRows, ctx);
  const total = totalCompletes(rows);
  const wanted = Number(ctx.wanted);
  const activeRows = interlocking ? rows : looseRows;

  // Feasibility from the server: the target group's figure and one count per quota row, from the same calculation.
  // The rows are sent as they are now (unsaved edits included) once they pass the page's own checks; the saved
  // basics, region and profiling complete the request.
  const quotaKey = Object.keys(errors).length === 0 && activeRows.length > 0
    ? JSON.stringify(interlocking ? quotasToPayload(rows, splits) : looseQuotasToPayload(looseRows))
    : '';
  const feasibilityInput = useMemo(
    () => buildFeasibilityInput({ basics: targetGroup.basicSetting, region: targetGroup.region, profilingAnswers: targetGroup.profilingAnswers, quota: quotaKey ? JSON.parse(quotaKey) : undefined }),
    [targetGroup.basicSetting, targetGroup.region, targetGroup.profilingAnswers, quotaKey]
  );
  const feasibility = useFeasibility(feasibilityInput);
  const cellFor = (index: number) => (feasibility.data ? feasibility.data.cells.find((c) => c.index === index) : undefined);

  // Live fill per quota row, once respondents use the entry link (statistics "source": "sessions"). The server matches rows
  // to the SAVED quota rows by position, so the column is shown only while the page still has as many rows as are saved.
  const stats = useAsyncData<ProjectStatistics | null>((signal) => getProjectStatistics(project.id, { signal }), [project.id, targetGroup.id]);
  const statsGroup = stats.data && stats.data.source === 'sessions' ? (stats.data.targetGroups || []).find((g) => g.targetGroupId === targetGroup.id) : undefined;
  const fillCells: QuotaCellProgress[] | undefined = statsGroup && statsGroup.cells && statsGroup.cells.length > 0 ? statsGroup.cells : undefined;
  const showFill = Boolean(fillCells) && fillCells!.length === activeRows.length;
  const fillFor = (index: number) => (showFill && fillCells ? fillCells.find((c) => c.index === index) : undefined);

  // Server messages refer to rows by position, so any edit makes them stale.
  const edited = () => setServerErrors(null);
  const update = (key: string, field: keyof QuotaRow, value: string | string[]) => { edited(); setRows((rs) => rs.map((r) => (r.key === key ? { ...r, [field]: value } : r))); };
  const addRow = () => { edited(); setRows((rs) => [...rs, { key: newKey(), minAge: String(basics.ageMinLimit), maxAge: String(basics.ageMaxLimit), male: '0', female: '0', stateIds: [], profilingAnswerIds: [] }]); };
  const removeRow = (key: string) => { edited(); setRows((rs) => rs.filter((r) => r.key !== key)); };
  const distribute = () => { edited(); setRows(autoDistribute({ ageMin: basics.ageMinLimit, ageMax: basics.ageMaxLimit, gender: basics.gender, completes: basics.noOfWantedCompletes, bands })); };

  const updateLoose = (key: string, patch: Partial<LooseRow>) => { edited(); setLooseRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r))); };
  const addLoose = (dimension: LooseDimension) => { edited(); setLooseRows((rs) => [...rs, newLooseRow(dimension)]); };
  const removeLoose = (key: string) => { edited(); setLooseRows((rs) => rs.filter((r) => r.key !== key)); };

  const changeMode = (next: string) => { edited(); setAttempted(false); setMode(next as QuotaMode); };

  // A preset was applied on the server: show exactly what it saved (its mode, rows and splits).
  const showApplied = (group: TargetGroup) => {
    const q = group.quotaSettings;
    const appliedMode = modeFromSaved(q);
    edited();
    setAttempted(false);
    setMode(appliedMode);
    if (appliedMode === 'interlocking') {
      const sp = splitsFromSaved(q);
      setRows(quotasFromSaved(q));
      setSplitRegion(sp.splitRegion);
      setSplitProfiling(sp.splitProfiling);
    } else {
      setLooseRows(looseFromSaved(q));
    }
    reload();
  };

  const save = (next: 'done' | 'exclusions') => {
    setAttempted(true);
    setFormError('');
    setServerErrors(null);
    if (Object.keys(errors).length) return undefined;
    return run(async () => {
      try {
        await saveQuotaSettings(project.id, targetGroup.id, interlocking ? quotasToPayload(rows, splits) : looseQuotasToPayload(looseRows));
        await reload();
        notify('Target group saved', { tone: 'ok' });
        goStep(next);
      } catch (err) {
        const body = (err as { body?: { message?: string; errors?: unknown } }).body;
        if (body && Array.isArray(body.errors)) {
          const { byRow, general } = parseQuotaErrors(body.errors, activeRows);
          setServerErrors({ message: body.message || '', byRow, general });
        } else {
          setFormError(errorMessage(err));
        }
      }
    });
  };

  // "Save as preset" needs the rows saved on the server. When the page's rows are valid they are saved first (in place,
  // staying on this step); otherwise the already saved quotas are used, if there are any.
  const pageRowsValid = activeRows.length > 0 && Object.keys(errors).length === 0;
  const presetSource: PresetSource = pageRowsValid ? 'page' : hasSavedQuotas ? 'saved' : 'none';
  const savePageQuotas = async () => {
    await saveQuotaSettings(project.id, targetGroup.id, interlocking ? quotasToPayload(rows, splits) : looseQuotasToPayload(looseRows));
    await reload();
  };

  const columns = 4 + (regionOn ? 1 : 0) + (profilingOn ? 1 : 0) + 2 + (showFill ? 1 : 0); // age, [regions], [profiling], male, female, total, panel, remove
  const serverRowProblems = serverErrors ? Object.keys(serverErrors.byRow).length : 0;
  const problems: SummaryProblems = {
    errors, warnings, attempted,
    serverMessage: serverErrors ? serverErrors.message : '',
    serverGeneral: serverErrors ? serverErrors.general : [],
    serverRowProblems,
  };

  return (
    <div>
      <div className="grid items-start gap-4 min-[1180px]:grid-cols-[minmax(0,1.9fr)_minmax(0,1fr)]">
        <Card className="overflow-hidden">
          <div className="flex flex-wrap items-start justify-between gap-3 px-5 pb-3 pt-5">
            <div>
              <h2 className="text-base font-semibold text-ink">Quotas</h2>
              <p className="mt-0.5 text-sm text-muted">
                {interlocking
                  ? `One row per cell. Ages stay within ${basics.ageMinLimit}-${basics.ageMaxLimit}.`
                  : `Each row is an independent quota on one dimension. Ages stay within ${basics.ageMinLimit}-${basics.ageMaxLimit}.`}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>Apply preset</Button>
              <Button variant="secondary" size="sm" onClick={() => setSavePresetOpen(true)}>Save as preset</Button>
              {interlocking && (
                <>
                  <label className="flex items-center gap-1.5 text-xs text-muted">
                    Bands
                    <select aria-label="Number of age bands" value={bands} onChange={(e) => setBands(Number(e.target.value))} className="rounded-md border border-line bg-surface px-2 py-1 text-sm text-ink">
                      {BAND_CHOICES.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  <Button variant="secondary" size="sm" onClick={distribute}>Auto-distribute {wanted.toLocaleString()}</Button>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 px-5 pb-3">
            <span className="text-xs text-muted">Mode</span>
            <Tabs tabs={MODE_TABS} value={mode} onChange={changeMode} label="Quota mode" />
            <span className="text-xs text-muted">{interlocking ? 'Rows combine their dimensions into cells that add up.' : 'Rows stand alone; none may exceed the wanted completes.'}</span>
          </div>

          {interlocking ? (
            <>
              <div className="flex flex-wrap items-center gap-2.5 px-5 pb-3">
                <span className="text-xs text-muted">Split by</span>
                <label className="flex items-center gap-2.5 rounded-lg border border-brand-300 bg-brand-50/60 px-3 py-1.5 text-sm font-semibold opacity-80">
                  <input type="checkbox" className="h-4 w-4 accent-brand-600" checked disabled aria-label="Age and gender" />
                  <span>Age &amp; gender<span className="block text-[11px] font-normal text-muted">always on</span></span>
                </label>
                <SplitSwitch label="Region" hint={canSplitRegion ? "this target group's states" : 'no regions saved: nationwide'} checked={regionOn} disabled={!canSplitRegion} onChange={(on) => { edited(); setSplitRegion(on); }} />
                <SplitSwitch label="Profiling" hint={canSplitProfiling ? 'its saved answers' : 'no profiling answers saved'} checked={profilingOn} disabled={!canSplitProfiling} onChange={(on) => { edited(); setSplitProfiling(on); }} />
              </div>

              {rows.length === 0 ? (
                <p className="border-t border-line px-4 py-10 text-center text-sm text-muted">No quotas yet. Add a row, or use Auto-distribute to start from an even split.</p>
              ) : (
                <Table caption="Quotas">
                  <thead>
                    <tr>
                      <Th>Age from</Th><Th>Age to</Th>
                      {regionOn && <Th>Regions</Th>}
                      {profilingOn && <Th>Profiling</Th>}
                      <Th numeric>Male</Th><Th numeric>Female</Th><Th numeric>Total</Th><Th numeric title="Matching panelists. A green tick means there are enough for this row's wanted completes.">Panel</Th>{showFill && <Th numeric title="Completes so far against this row's wanted completes, from respondents.">Filled</Th>}<Th><span className="sr-only">Remove</span></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const err = attempted ? errors[r.key] : undefined;
                      const serverRow = serverErrors ? serverErrors.byRow[r.key] : undefined;
                      const flagged = Boolean(err || serverRow);
                      const label = `row ${i + 1}`;
                      return (
                        <React.Fragment key={r.key}>
                          <tr className={flagged ? 'bg-danger-soft/40' : undefined}>
                            {([['minAge', 'Age from'], ['maxAge', 'Age to']] as const).map(([f, l]) => (
                              <Td key={f} className="py-1.5"><input aria-label={`${l}, ${label}`} inputMode="numeric" className={cx(cell, 'text-left', flagged && 'border-danger')} value={r[f]} onChange={(e) => update(r.key, f, e.target.value)} /></Td>
                            ))}
                            {regionOn && (
                              <Td className="py-1.5"><CellPicker label={`Regions, ${label}`} emptyText="All regions" groups={stateGroups} selected={r.stateIds} onChange={(ids) => update(r.key, 'stateIds', ids)} /></Td>
                            )}
                            {profilingOn && (
                              <Td className="py-1.5"><CellPicker label={`Profiling, ${label}`} emptyText="Everyone" groups={answerGroups} selected={r.profilingAnswerIds} onChange={(ids) => update(r.key, 'profilingAnswerIds', ids)} /></Td>
                            )}
                            {([['male', 'Male completes'], ['female', 'Female completes']] as const).map(([f, l]) => (
                              <Td key={f} numeric className="py-1.5"><input aria-label={`${l}, ${label}`} inputMode="numeric" className={cx(cell, flagged && 'border-danger')} value={r[f]} onChange={(e) => update(r.key, f, e.target.value)} /></Td>
                            ))}
                            <Td numeric className="py-1.5 font-semibold">{rowTotal(r)}</Td>
                            <Td numeric className="py-1.5"><CellFeasibility cell={cellFor(i)} result={feasibility.data} /></Td>
                            {showFill && <Td numeric className="py-1.5"><CellFill cell={fillFor(i)} /></Td>}
                            <Td className="py-1.5 text-right"><Button variant="ghost" size="sm" aria-label={`Remove ${label}`} onClick={() => removeRow(r.key)}>✕</Button></Td>
                          </tr>
                          {err && <tr><td colSpan={columns} className="bg-danger-soft px-4 py-1.5 text-xs text-danger" role="alert">Row {i + 1}: {err}</td></tr>}
                          {serverRow && serverRow.map((m) => <tr key={m}><td colSpan={columns} className="bg-danger-soft px-4 py-1.5 text-xs text-danger" role="alert">Row {i + 1}: {m}</td></tr>)}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-2.5">
                <Button variant="ghost" size="sm" onClick={addRow}>＋ Add row</Button>
                <span className="text-xs text-muted">Saved as: <b>{quotasToPayload([], splits).quota as string}</b></span>
              </div>
            </>
          ) : (
            <>
              <LooseQuotaTable
                rows={looseRows}
                attempted={attempted}
                errors={errors}
                serverRowErrors={serverErrors ? serverErrors.byRow : {}}
                stateGroups={stateGroups}
                answerGroups={answerGroups}
                canRegion={canSplitRegion}
                canProfiling={canSplitProfiling}
                onUpdate={updateLoose}
                onAdd={addLoose}
                onRemove={removeLoose}
                cellFor={cellFor}
                fillFor={showFill ? fillFor : undefined}
                result={feasibility.data}
              />
              <div className="flex justify-end border-t border-line px-4 py-2.5">
                <span className="text-xs text-muted">Saved as: <b>{looseQuotasToPayload(looseRows).quota as string}</b></span>
              </div>
            </>
          )}
        </Card>

        {interlocking
          ? <QuotaSummary total={total} wanted={wanted} rows={rows} splitActive={regionOn || profilingOn} feasibility={feasibility} {...problems} />
          : <LooseSummary rows={looseRows} wanted={wanted} feasibility={feasibility} {...problems} />}
      </div>

      {formError && <div className="mt-4"><InlineAlert>{formError}</InlineAlert></div>}
      <div className="mt-4 flex justify-between">
        <Button variant="secondary" onClick={() => goStep('profiling')}>← Back</Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => save('exclusions')} disabled={submitting}>Save &amp; set exclusions →</Button>
          <Button onClick={() => save('done')} loading={submitting}>Save target group</Button>
        </div>
      </div>

      <QuotaPresetPicker open={pickerOpen} projectId={project.id} targetGroupId={targetGroup.id} onClose={() => setPickerOpen(false)} onApplied={showApplied} />
      <SaveQuotaPresetDialog open={savePresetOpen} targetGroupId={targetGroup.id} source={presetSource} savePageQuotas={savePageQuotas} suggestedName={targetGroup.name ? `${targetGroup.name} quotas` : ''} onClose={() => setSavePresetOpen(false)} />
    </div>
  );
}
